import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { PurchaseOrderApi, POSkuApi } from '../types';
import DatePicker from '../components/DatePicker';

const API_BASE = import.meta.env.VITE_API_BASE_URL;
const getHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('token')}`,
});

interface SKURow {
  id: string;
  isNew?: boolean;
  sku_number: string;
  description: string;
  product_category: string;
  vendor_code: string;
  work_order: string;
  color: string;
  size: string;
  total_qty: number;
  qty_per_pack: number;
  unit_price: number;
}

const EditPurchaseOrder: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerLocation, setCustomerLocation] = useState('');
  const [workOrder, setWorkOrder] = useState('');
  const [shipDate, setShipDate] = useState('');
  const [skuRows, setSkuRows] = useState<SKURow[]>([]);

  useEffect(() => {
    const fetchPO = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE}/api/v1/purchase-orders/${id}`, { headers: getHeaders() });
        const data = await response.json();
        if (data.success) {
          const po: PurchaseOrderApi = data.data;
          setPoNumber(po.po_number);
          setCustomerName(po.customer_name);
          setCustomerLocation(po.customer_location || '');
          setWorkOrder(po.work_order || '');
          setShipDate(po.ship_date || '');
          setSkuRows(
            (po.skus || []).map((sku: POSkuApi) => ({
              id: sku.id,
              sku_number: sku.sku_number,
              description: sku.description || '',
              product_category: sku.product_category || '',
              vendor_code: sku.vendor_code || '',
              work_order: sku.work_order || '',
              color: sku.color || '',
              size: sku.size || '',
              total_qty: sku.total_qty,
              qty_per_pack: sku.qty_per_pack,
              unit_price: Number(sku.unit_price) || 0,
            }))
          );
        }
      } catch (err) {
        setError('Failed to load purchase order');
      } finally {
        setLoading(false);
      }
    };
    fetchPO();
  }, [id]);

  const addRow = () => {
    const newRow: SKURow = {
      id: `new_${Math.random().toString(36).substr(2, 9)}`,
      isNew: true,
      sku_number: '',
      description: '',
      product_category: '',
      vendor_code: '',
      work_order: '',
      color: '',
      size: '',
      total_qty: 0,
      qty_per_pack: 0,
      unit_price: 0,
    };
    setSkuRows([...skuRows, newRow]);
  };

  const removeRow = (rowId: string) => {
    if (skuRows.length > 1) {
      setSkuRows(skuRows.filter(row => row.id !== rowId));
    }
  };

  const updateRow = (rowId: string, field: keyof SKURow, value: any) => {
    setSkuRows(skuRows.map(row => (row.id === rowId ? { ...row, [field]: value } : row)));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      const response = await fetch(`${API_BASE}/api/v1/purchase-orders/${id}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({
          po_number: poNumber,
          customer_name: customerName,
          customer_location: customerLocation,
          work_order: workOrder || null,
          ship_date: shipDate || null,
          skus: skuRows.map(row => ({
            id: row.isNew ? undefined : row.id,
            sku_number: row.sku_number,
            description: row.description,
            product_category: row.product_category,
            vendor_code: row.vendor_code,
            work_order: row.work_order || null,
            color: row.color,
            size: row.size,
            total_qty: row.total_qty,
            qty_per_pack: row.qty_per_pack,
            unit_price: row.unit_price || null,
          })),
        }),
      });

      const data = await response.json();
      if (data.success) {
        navigate(`/purchase-orders/${id}`);
      } else {
        setError(data.message || 'Failed to update purchase order');
      }
    } catch (err) {
      setError('Failed to update purchase order. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <span className="material-symbols-outlined animate-spin text-[24px] text-slate-400">progress_activity</span>
        <span className="ml-2 text-slate-500">Loading...</span>
      </div>
    );
  }

  return (
    <div className="w-full">
      <nav aria-label="Breadcrumb" className="mb-6 flex text-sm text-slate-500">
        <ol className="inline-flex items-center space-x-1 md:space-x-3">
          <li className="inline-flex items-center">
            <Link to="/dashboard" className="inline-flex items-center hover:text-slate-900">
              <span className="material-symbols-outlined mr-2 text-[18px]">dashboard</span>
              Dashboard
            </Link>
          </li>
          <li>
            <div className="flex items-center">
              <span className="material-symbols-outlined text-[18px] text-slate-400">chevron_right</span>
              <Link to="/purchase-orders" className="ml-1 hover:text-slate-900 md:ml-2">Purchase Orders</Link>
            </div>
          </li>
          <li>
            <div className="flex items-center">
              <span className="material-symbols-outlined text-[18px] text-slate-400">chevron_right</span>
              <Link to={`/purchase-orders/${id}`} className="ml-1 hover:text-slate-900 md:ml-2">{poNumber}</Link>
            </div>
          </li>
          <li aria-current="page">
            <div className="flex items-center">
              <span className="material-symbols-outlined text-[18px] text-slate-400">chevron_right</span>
              <span className="ml-1 font-medium text-[#136bce] md:ml-2">Edit Purchase Order</span>
            </div>
          </li>
        </ol>
      </nav>

      <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Edit Purchase Order</h1>
          <p className="mt-1 text-sm text-slate-500">Modify PO record for {poNumber}</p>
        </div>
      </div>

      {error && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 flex items-center gap-2">
          <span className="material-symbols-outlined text-[18px]">error</span>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="mb-8 rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center gap-2 border-b border-slate-200 px-6 py-4">
            <span className="material-symbols-outlined text-slate-400">info</span>
            <h2 className="text-base font-semibold text-slate-900">Basic Details</h2>
          </div>
          <div className="grid grid-cols-1 gap-8 p-6 md:grid-cols-2 lg:grid-cols-5">
            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor="po-number">Purchase Order Number</label>
              <input
                className="rounded-lg border-slate-300 text-sm placeholder:text-slate-400 focus:border-[#136bce] focus:ring-[#136bce] shadow-sm"
                id="po-number"
                type="text"
                value={poNumber}
                onChange={(e) => setPoNumber(e.target.value)}
                required
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor="customer-name">Customer Name</label>
              <input
                className="rounded-lg border-slate-300 text-sm placeholder:text-slate-400 focus:border-[#136bce] focus:ring-[#136bce] shadow-sm"
                id="customer-name"
                type="text"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                required
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor="customer-location">Location</label>
              <input
                className="rounded-lg border-slate-300 text-sm placeholder:text-slate-400 focus:border-[#136bce] focus:ring-[#136bce] shadow-sm"
                id="customer-location"
                type="text"
                value={customerLocation}
                onChange={(e) => setCustomerLocation(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor="work-order">Work Order</label>
              <input
                className="rounded-lg border-slate-300 text-sm placeholder:text-slate-400 focus:border-[#136bce] focus:ring-[#136bce] shadow-sm"
                id="work-order"
                type="text"
                value={workOrder}
                onChange={(e) => setWorkOrder(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500" htmlFor="ship-date">Ship Date</label>
              <div className="relative">
                <DatePicker
                  value={shipDate}
                  onChange={(date) => setShipDate(date)}
                  placeholder="Select ship date"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="mb-8 flex flex-col rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-6 py-4">
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-slate-400">inventory_2</span>
              <h2 className="text-base font-semibold text-slate-900">SKU Details</h2>
              <span className="ml-2 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">{skuRows.length} Items</span>
            </div>
            <button
              className="flex items-center gap-2 rounded-lg bg-[#136bce] px-4 py-2 text-xs font-semibold text-white shadow-md transition-colors hover:bg-blue-700"
              type="button"
              onClick={addRow}
            >
              <span className="material-symbols-outlined text-[16px]">add</span>
              Add SKU
            </button>
          </div>
          <div className="custom-scrollbar overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-white">
                <tr>
                  <th className="w-36 px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">SKU Number</th>
                  <th className="min-w-[200px] px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Description</th>
                  <th className="w-40 px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Category</th>
                  <th className="w-32 px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Vendor Code</th>
                  <th className="w-40 px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Work Order</th>
                  <th className="w-28 px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Color</th>
                  <th className="w-24 px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Size</th>
                  <th className="w-32 px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">Total Qty</th>
                  <th className="w-32 px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">Qty / Pack</th>
                  <th className="w-28 px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">Packs</th>
                  <th className="w-20 px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white">
                {skuRows.map((row) => {
                  const packs = row.qty_per_pack > 0 ? row.total_qty / row.qty_per_pack : 0;
                  const packsDisplay = packs ? Number(packs.toFixed(2)).toString() : '';
                  return (
                    <tr key={row.id} className="transition-colors hover:bg-slate-50">
                      <td className="align-top px-6 py-3">
                        <input
                          className="sku-input"
                          placeholder="SKU-XXX"
                          type="text"
                          value={row.sku_number}
                          onChange={(e) => updateRow(row.id, 'sku_number', e.target.value)}
                        />
                      </td>
                      <td className="align-top px-6 py-3">
                        <input
                          className="sku-input"
                          placeholder="Product description"
                          type="text"
                          value={row.description}
                          onChange={(e) => updateRow(row.id, 'description', e.target.value)}
                        />
                      </td>
                      <td className="align-top px-6 py-3">
                        <input
                          className="sku-input"
                          placeholder="Category"
                          type="text"
                          value={row.product_category}
                          onChange={(e) => updateRow(row.id, 'product_category', e.target.value)}
                        />
                      </td>
                      <td className="align-top px-6 py-3">
                        <input
                          className="sku-input"
                          placeholder="Vendor Code"
                          type="text"
                          value={row.vendor_code}
                          onChange={(e) => updateRow(row.id, 'vendor_code', e.target.value)}
                        />
                      </td>
                      <td className="align-top px-6 py-3">
                        <input
                          className="sku-input"
                          placeholder="Work Order"
                          type="text"
                          value={row.work_order}
                          onChange={(e) => updateRow(row.id, 'work_order', e.target.value)}
                        />
                      </td>
                      <td className="align-top px-6 py-3">
                        <input
                          className="sku-input"
                          placeholder="Color"
                          type="text"
                          value={row.color}
                          onChange={(e) => updateRow(row.id, 'color', e.target.value)}
                        />
                      </td>
                      <td className="align-top px-6 py-3">
                        <input
                          className="sku-input"
                          placeholder="Size"
                          type="text"
                          value={row.size}
                          onChange={(e) => updateRow(row.id, 'size', e.target.value)}
                        />
                      </td>
                      <td className="align-top px-6 py-3">
                        <input
                          className="sku-input text-center"
                          min="0"
                          placeholder="0"
                          type="number"
                          value={row.total_qty || ''}
                          onChange={(e) => updateRow(row.id, 'total_qty', parseInt(e.target.value) || 0)}
                        />
                      </td>
                      <td className="align-top px-6 py-3">
                        <input
                          className="sku-input text-center"
                          min="0"
                          placeholder="0"
                          type="number"
                          value={row.qty_per_pack || ''}
                          onChange={(e) => updateRow(row.id, 'qty_per_pack', parseInt(e.target.value) || 0)}
                        />
                      </td>
                      <td className="align-top px-6 py-3">
                        <input
                          className="sku-input border-slate-300 bg-slate-50 text-center text-slate-500"
                          placeholder="0"
                          readOnly
                          type="text"
                          value={packsDisplay}
                        />
                      </td>
                      <td className="align-middle px-6 py-3 text-center">
                        <button
                          className={`rounded-full p-2 transition-colors ${skuRows.length <= 1 ? 'text-slate-200 cursor-not-allowed' : 'text-slate-400 hover:bg-red-50 hover:text-red-500'}`}
                          type="button"
                          disabled={skuRows.length <= 1}
                          onClick={() => removeRow(row.id)}
                          title={skuRows.length <= 1 ? 'Cannot delete the last SKU' : 'Delete SKU'}
                        >
                          <span className="material-symbols-outlined text-[20px]">delete</span>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div
            className="flex cursor-pointer justify-center border-t border-slate-200 bg-slate-50 px-6 py-3 transition-colors hover:bg-slate-100"
            onClick={addRow}
          >
            <div className="flex items-center text-sm font-medium text-[#136bce]">
              <span className="material-symbols-outlined mr-1 text-[20px]">add_circle</span>
              Add New Row
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-4 py-4">
          <button
            className="rounded-lg border border-slate-300 bg-white px-6 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:bg-slate-50 hover:text-slate-900"
            type="button"
            onClick={() => navigate(`/purchase-orders/${id}`)}
          >
            Cancel
          </button>
          <button
            className="flex items-center gap-2 rounded-lg bg-[#136bce] px-6 py-2.5 text-sm font-semibold text-white shadow-lg shadow-blue-500/30 transition-colors hover:bg-blue-700 disabled:opacity-50"
            type="submit"
            disabled={saving}
          >
            {saving ? (
              <>
                <span className="material-symbols-outlined animate-spin text-[20px]">progress_activity</span>
                Updating...
              </>
            ) : (
              <>
                <span className="material-symbols-outlined text-[20px]">save</span>
                Update Purchase Order
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditPurchaseOrder;
